import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";

export default function FileUpload({ onFileUpload }) {
  const [filename, setFilename] = useState("");
  const [csvData, setCsvData] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const toCamelCase = (str) => {
    if (!str) return "";
    return str
      .replace(/[^a-zA-Z0-9]+/g, " ")
      .replace(/\b(\w+)\b/g, (match) => match.toLowerCase())
      .replace(/ (\w)/g, (_, letter) => letter.toUpperCase())
      .trim();
  };

  const getParseCSV = (text) => {
    const rows = text.split("\n");
    const rawHeaders = rows[0].split(",").map((header) => header.trim());
    const headers = rawHeaders.map(toCamelCase);
    const csvRegex = /,(?=(?:(?:[^"]*"){2})*[^"]*$)/;

    const data = rows.slice(1).map((row) => {
      const values = row.split(csvRegex);
      const entry = {};
      headers.forEach((header, index) => {
        const value = values[index]
          ? values[index].replace(/^"|"$/g, "").trim()
          : "";
        entry[header] = value;
      });
      return entry;
    });

    return data.filter((entry) => Object.keys(entry).length > 0);
  };

  const processFile = (file) => {
    if (!file) return;

    const reader = new FileReader();

    if (file.type === "text/csv" || file.name.endsWith(".csv")) {
      reader.onload = (e) => {
        const text = e.target.result;
        const parsedData = getParseCSV(text);
        setCsvData(parsedData);
        setFilename(file.name);
        if (onFileUpload) onFileUpload(parsedData);
      };
      reader.readAsText(file);
    } else if (
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.name.endsWith(".xlsx")
    ) {
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        const formattedData = jsonData.map((row) => {
          const newRow = {};
          Object.keys(row).forEach((key) => {
            newRow[toCamelCase(key)] = row[key];
          });
          return newRow;
        });

        setCsvData(formattedData);
        setFilename(file.name);
        if (onFileUpload) onFileUpload(formattedData);
      };
      reader.readAsArrayBuffer(file);
    } else {
      setFilename("");
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    processFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files[0];

    console.log("FILE", file);
    processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleClearFile = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setFilename("");
    setCsvData([]);
    if (onFileUpload) onFileUpload([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={`border  ${dragActive ? "border-primary" : "border-light"}`}
      style={{
        borderStyle: "dashed",
        borderRadius: "8px",
        textAlign: "center",
      }}
    >
      <label
        htmlFor="file-upload"
        className="btn bg-white border border-primary text-primary d-flex align-items-center mx-auto"
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "220px", // made it wider
        }}
        title={filename || "Import"}
        key={filename} // ⬅️ Force re-render when filename updates
      >
        <i className="bi bi-arrow-down-square" /> &nbsp;
        <div
          className="d-flex position-relative text-truncate"
          style={{ flexGrow: 1 }}
        >
          <span className="text-truncate" style={{ maxWidth: "160px" }}>
            {filename || "Import"}
          </span>
          {filename && (
            <i
              className="bi bi-x-circle text-danger"
              style={{
                position: "absolute",
                right: "0",
                width: "22px",
                height: "22px",
                backgroundColor: "white",
                borderRadius: "50%",
                cursor: "pointer",
                zIndex: 10,
              }}
              onClick={handleClearFile}
            />
          )}
        </div>
      </label>

      <input
        id="file-upload"
        type="file"
        accept=".csv,.xlsx"
        onChange={handleFileUpload}
        ref={fileInputRef}
        style={{ display: "none" }}
      />
    </div>
  );
}
